import { Shimmer } from "@/components/ai-elements/shimmer";
import { Button } from "@/components/ui/button";
import { useToastError } from "@/components/ui/use-toast";
import { ThemeProviderContext } from "@/contexts/theme-provider";
import { useApi } from "@/hooks/useApi";
import { useTranslation } from "@/hooks/useTranslation";
import {
  DirectionsRenderer,
  GoogleMap,
  InfoWindow,
  Marker,
  useJsApiLoader,
} from "@react-google-maps/api";
import { CompanyDTO, ItineraryDTO, WaypointDTO } from "@shaarp/common";
import { ExternalLink, Loader2 } from "lucide-react";
import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

export default function MapCompanies({
  companies,
  selected = undefined,
  setSelected = () => {},
  shouldCalculateItinerary = false,
}: {
  companies: CompanyDTO[];
  selected?: CompanyDTO;
  setSelected?: (company: CompanyDTO) => void;
  shouldCalculateItinerary?: boolean;
}) {
  const context = useContext(ThemeProviderContext);
  const { t } = useTranslation();
  const api = useApi();
  const toastError = useToastError();

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [waypoints, setWaypoints] = useState<WaypointDTO[]>([]);
  const [origin, setOrigin] = useState<google.maps.LatLngLiteral | undefined>(
    undefined
  );
  const [destination, setDestination] = useState<
    google.maps.LatLngLiteral | undefined
  >(undefined);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
  });

  const [map, setMap] = useState<google.maps.Map | null>(null);
  const [directionsResponse, setDirectionsResponse] =
    useState<google.maps.DirectionsResult>();

  // Calculate center based on companies
  const mapCenter = useMemo(() => {
    const companiesWithLocation = companies.filter(
      (company) => !!company.location.latitude && !!company.location.longitude
    );

    if (companiesWithLocation.length === 0) {
      // Default to France center if no companies
      return { lat: 46.603354, lng: 1.888334 };
    }

    const avgLat =
      companiesWithLocation.reduce(
        (sum, company) => sum + (company.location.latitude as number),
        0
      ) / companiesWithLocation.length;

    const avgLng =
      companiesWithLocation.reduce(
        (sum, company) => sum + (company.location.longitude as number),
        0
      ) / companiesWithLocation.length;

    return { lat: avgLat, lng: avgLng };
  }, [companies]);

  const onLoad = useCallback(function callback(map: google.maps.Map) {
    // This is just an example of getting and using the map instance!!! don't just blindly copy!
    // const bounds = new window.google.maps.LatLngBounds(center);
    // map.fitBounds(bounds);
    setMap(map);
  }, []);

  const onUnmount = useCallback(function callback() {
    setMap(null);
  }, []);

  useEffect(() => {
    if (
      map &&
      selected &&
      selected.location.latitude &&
      selected.location.longitude
    ) {
      map.fitBounds(
        new window.google.maps.LatLngBounds({
          lat: selected.location.latitude,
          lng: selected.location.longitude,
        })
      );
      map.setZoom(15);
    }
  }, [selected, map]);

  useEffect(() => {
    if (!map) return;

    if (!context.isLightTheme) {
      map.setOptions({
        styles: [
          { elementType: "geometry", stylers: [{ color: "#242f3e" }] },
          {
            elementType: "labels.text.stroke",
            stylers: [{ color: "#242f3e" }],
          },
          { elementType: "labels.text.fill", stylers: [{ color: "#746855" }] },
          {
            featureType: "administrative.locality",
            elementType: "labels.text.fill",
            stylers: [{ color: "#d59563" }],
          },
          {
            featureType: "poi",
            elementType: "labels.text.fill",
            stylers: [{ color: "#d59563" }],
          },
          {
            featureType: "poi.park",
            elementType: "geometry",
            stylers: [{ color: "#263c3f" }],
          },
          {
            featureType: "poi.park",
            elementType: "labels.text.fill",
            stylers: [{ color: "#6b9a76" }],
          },
          {
            featureType: "road",
            elementType: "geometry",
            stylers: [{ color: "#38414e" }],
          },
          {
            featureType: "road",
            elementType: "geometry.stroke",
            stylers: [{ color: "#212a37" }],
          },
          {
            featureType: "road",
            elementType: "labels.text.fill",
            stylers: [{ color: "#9ca5b3" }],
          },
          {
            featureType: "road.highway",
            elementType: "geometry",
            stylers: [{ color: "#746855" }],
          },
          {
            featureType: "road.highway",
            elementType: "geometry.stroke",
            stylers: [{ color: "#1f2835" }],
          },
          {
            featureType: "road.highway",
            elementType: "labels.text.fill",
            stylers: [{ color: "#f3d19c" }],
          },
          {
            featureType: "transit",
            elementType: "geometry",
            stylers: [{ color: "#2f3948" }],
          },
          {
            featureType: "transit.station",
            elementType: "labels.text.fill",
            stylers: [{ color: "#d59563" }],
          },
          {
            featureType: "water",
            elementType: "geometry",
            stylers: [{ color: "#17263c" }],
          },
          {
            featureType: "water",
            elementType: "labels.text.fill",
            stylers: [{ color: "#515c6d" }],
          },
          {
            featureType: "water",
            elementType: "labels.text.stroke",
            stylers: [{ color: "#17263c" }],
          },
        ],
      });
    } else {
      map.setOptions({
        styles: [],
      });
    }
  }, [map, context.isLightTheme]);

  // Function to fit bounds to all companies
  const fitBoundsToCompanies = useCallback(() => {
    if (!map || companies.length === 0) return;

    const companiesWithLocation = companies.filter(
      (company) => !!company.location.latitude && !!company.location.longitude
    );

    if (companiesWithLocation.length === 0) return;

    const bounds = new google.maps.LatLngBounds();
    companiesWithLocation.forEach((company) => {
      bounds.extend({
        lat: company.location.latitude as number,
        lng: company.location.longitude as number,
      });
    });
    map.fitBounds(bounds);
  }, [map, companies]);

  // Fit bounds to show all companies when map is opened or when itinerary is hidden
  useEffect(() => {
    if (!shouldCalculateItinerary) {
      fitBoundsToCompanies();
    }
  }, [map, companies, shouldCalculateItinerary, fitBoundsToCompanies]);

  useEffect(() => {
    if (shouldCalculateItinerary) {
      getDirections();
    } else {
      setDirectionsResponse(undefined);
    }
  }, [shouldCalculateItinerary, companies]);

  async function getDirections() {
    try {
      setIsLoading(true);
      const companiesLocations = companies
        .filter(
          (company) =>
            !!company.location.latitude && !!company.location.longitude
        )
        .map((company) => ({
          location: {
            lat: company.location.latitude as number,
            lng: company.location.longitude as number,
          },
          stopover: true,
        }));

      const { data: itinerary } = await api.post<ItineraryDTO>(
        `/companies/itinerary`,
        {
          waypoints: companiesLocations,
        }
      );

      const origin = itinerary.waypoints.shift()?.location;
      const destination = itinerary.waypoints.pop()?.location;

      setWaypoints(itinerary.waypoints);
      setOrigin(origin);
      setDestination(destination);

      const directionsService = new google.maps.DirectionsService();
      const result = await directionsService.route({
        origin: origin as google.maps.LatLngLiteral,
        destination: destination as google.maps.LatLngLiteral,
        travelMode: google.maps.TravelMode.DRIVING,
        waypoints: itinerary.waypoints,
      });
      setDirectionsResponse(result);

      // Fit bounds to show all points of the itinerary
      if (map && origin && destination) {
        const bounds = new google.maps.LatLngBounds();
        bounds.extend(origin);
        bounds.extend(destination);
        itinerary.waypoints.forEach((waypoint) => {
          bounds.extend(waypoint.location);
        });
        map.fitBounds(bounds);
      }
    } catch (error) {
      toastError();
    } finally {
      setIsLoading(false);
    }
  }

  function openInGoogleMaps() {
    const waypointsString = waypoints
      .map((waypoint) => `${waypoint.location.lat},${waypoint.location.lng}`)
      .join("|");
    window.open(
      `https://www.google.com/maps/dir/?api=1&travelmode=driving&origin=${origin?.lat},${origin?.lng}&destination=${destination?.lat},${destination?.lng}&waypoints=${waypointsString}`,
      "_blank"
    );
  }

  return (
    isLoaded && (
      <div className="relative h-full">
        {shouldCalculateItinerary && isLoading ? (
          <div className="flex absolute inset-0 flex-col gap-3 justify-center items-center backdrop-blur-xs bg-background/95">
            <Loader2 className="w-8 h-8 text-indigo-500 animate-spin" />
            <Shimmer duration={1.5} className="text-sm font-medium">
              {t("company.map.searchingBestItinerary")}
            </Shimmer>
          </div>
        ) : (
          <>
            <GoogleMap
              mapContainerStyle={{ width: "100%", height: "100%" }}
              center={mapCenter}
              zoom={companies.length === 0 ? 6 : 9}
              onLoad={onLoad}
              onUnmount={onUnmount}
            >
              {directionsResponse && (
                <DirectionsRenderer
                  directions={directionsResponse}
                  options={{
                    suppressMarkers: true,
                  }}
                />
              )}
              {companies
                .filter(
                  (company) =>
                    !!company.location.latitude && !!company.location.longitude
                )
                .map((company) => (
                  <Marker
                    position={{
                      lat: company.location.latitude as number,
                      lng: company.location.longitude as number,
                    }}
                    onClick={() => setSelected(company)}
                    key={company.id}
                  >
                    {selected && selected.id === company.id && (
                      <InfoWindow>
                        <div className="space-y-3 text-center text-black">
                          <div className="font-medium text-md size-md">
                            {company.name}
                          </div>
                          <div>
                            {company.location.address.line1}
                            <br />
                            {company.location.address.zipCode}{" "}
                            {company.location.address.city}
                          </div>
                          <Button asChild className="w-full">
                            <Link to={`/company/${company.id}`} target="_blank">
                              {t("company.map.view")}
                            </Link>
                          </Button>
                        </div>
                      </InfoWindow>
                    )}
                  </Marker>
                ))}
            </GoogleMap>
            {origin && destination && (
              <Button
                className="absolute bottom-6 right-16"
                onClick={openInGoogleMaps}
              >
                {t("company.map.openInGoogleMaps")}
                <ExternalLink className="ml-2 w-4 h-4" />
              </Button>
            )}
          </>
        )}
      </div>
    )
  );
}
